<template>
	<div class="nagios-mini">
		<div class="nagios-container">
			<div class="nagios-summary" v-bind:class="summaryClass">
				<h2>Infrastructure Availability</h2>
				<div class="nagios-up-pct" v-bind:class="upClass">
					{{ upPct }}
				</div>
			</div>
			<div v-show="mode == 'OK'" class="nagios-all-up">
				<h2>All {{ itemCount }} monitored items are up!</h2>
				<p>Last event: {{ lastEventItem }}</p>
				<p>{{ lastEventTime }}</p>
			</div>
			<div v-show="mode == 'Problem'" class="nagios-problems">
				<div class="nagios-host" v-for="(value, key) in problems" :key="key">
					<h2>{{ value.host.displayName }}: <span v-bind:class="value.host.status | asHostCssClass">{{ describeHostState(value.host.status) }}</span></h2>
					<div class="services" v-show="value.services.length > 0">
						<div class="service" v-bind:class="service.status | asServiceCssClass" v-for="service in value.services" :key="service.name">{{ service.description }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import Bugsnag from '@bugsnag/js'

	let refreshInterval = null;
	let newAlert = new Audio("sounds/new-alert.mp3");
	let clearAlert = new Audio("sounds/clear-alert.mp3");

	export default {
		name: 'MiniNagios',
		data() {
			return {
				mode: null,
				upPct: "—",
				upClass: "",
				summaryClass: "ok",
				itemCount: null,
				lastEventItem: null,
				lastEventTime: null,
				problemCount: 0,
				problems: {}
			}
		},

		filters: {
			asHostCssClass: function (value) {
				switch (value) {
					case 0:
						return "up";
					case 1:
						return "down";
					case 2:
						return "unreachable";
					default:
						return "";
				}
			},
			asServiceCssClass: function (value) {
				switch (value) {
					case 0:
						return "ok";
					case 1:
						return "warning";
					case 2:
						return "critical";
					case 3:
						return "unknown";
					default:
						return "";
				}
			}
		},

		mounted() {
			console.log("Nagios mini module initiated");
			const self = this;
			self.getStatus();
			refreshInterval = setInterval(self.getStatus, 1000 * 30);
			window.addEventListener('nagiosSummary', (e) => {
				self.updateStatus(e.detail);
			});
		},

		destroyed() {
			console.log("Stopping Nagios mini module");
			const self = this;
			window.removeEventListener('nagiosSummary', (e) => {
				self.updateStatus(e.detail);
			});
			clearInterval(refreshInterval);
		},

		methods: {
			getStatus() {
				const self = this;
				this.$http.get('/icinga/summary').then(response => {
					let data = response.body;
					self.updateStatus(data);
				}).catch(e => {
					Bugsnag.notify(new Error(e.url), function (event) {
						event.addMetadata('httpResponse', e);
					});
					console.error(e);
				});
			},
			updateStatus(data) {
				const self = this;

				if (data.problemCount < this.problemCount) {
					this.soundClearAlert();
				}

				let speakAlerts = [];

				// See if there are new problems to speak
				for (var key in data.problems) {
					if (!this.problems.hasOwnProperty(key)) {
						// If problems for this host are new to the list, add them to the speaking list
						speakAlerts.push(self.addToSpeakingList(key, data.problems[key]));
					} else {
						// Determine if there are new services to speak about
						let newServices = [];
						for (var x = 0, len = data.problems[key].services.length; x < len; x++) {
							let svc = data.problems[key].services[x];
							let s = this.problems[key].services.find(f => {
								return f.name === svc.name;
							});
							if (!s) newServices.push(svc);
						}

						// Add new services to the speaking list
						if (newServices.length > 0) {
							let obj = {
								host: data.problems[key].host,
								services: newServices
							}
							speakAlerts.push(self.addToSpeakingList(key, obj));
						}
					}
				}

				// If there are new alerts to speak, formulate the string and speak them
				if (speakAlerts.length > 0) {
					this.soundNewAlert();
					let speakText = speakAlerts.join("...", speakAlerts);
					setTimeout(() => {
						self.speakAlert(speakText);
					}, 2000);
				}

				this.upPct = data.uptimePercentage + "%";
				this.itemCount = data.itemCount;
				this.lastEventItem = data.mostRecentEventDescription;
				this.lastEventTime = data.mostRecentEventRelativeTime;


				if (data.uptimePercentage < 90) this.upClass = "few-up";
				else if (data.uptimePercentage < 100) this.upClass = "some-up";
				else this.upClass = "all-up";

				if (data.problemCount > 0) {
					this.mode = "Problem"
					this.summaryClass = "problems";
				} else {
					this.mode = "OK"
					this.summaryClass = "ok";
				}

				this.problems = data.problems;
				this.problemCount = data.problemCount;
			},

			describeHostState(val) {
				switch (val) {
					case 0:
						return "Up";
					case 1:
						return "Down";
					case 2:
						return "Unreachable";
					default:
						return "Unknown";
				}
			},

			describeServiceState(val) {
				switch (val) {
					case 0:
						return "OK";
					case 1:
						return "Warning";
					case 2:
						return "Critical";
					case 3:
						return "Unknown";
					default:
						return "Unknown";
				}
			},

			soundNewAlert() {
				if (!window.audio) return;
				newAlert.currentTime = 0;
				newAlert.play();
			},

			soundClearAlert() {
				if (!window.audio) return;
				clearAlert.currentTime = 0;
				clearAlert.play();
			},

			addToSpeakingList(key, value) {
				let hostName = value.host && value.host.displayName ? value.host.displayName : key;
				let text = '';
				text += `${hostName}. `;
				if (value.host && value.host.status !== 0) text += `Host ${this.describeHostState(value.host.status)}. `;
				else {
					let serviceText = '';
					for (var x = 0, len = value.services.length; x < len; x++) {
						let svc = value.services[x];
						if (serviceText.length > 0) serviceText += 'And ';
						serviceText += `${svc.description}. `
						serviceText += `${this.describeServiceState(svc.status)}. `
					}
					text += serviceText;
				}
				return text;
			},

			speakAlert(text) {
				let audio = new Audio("/polly/speak?text=" + encodeURIComponent(text));
				audio.addEventListener("canplaythrough", () => {
					audio.play();
				});
			}
		}
	}
</script>

<style lang="less">
	@import '../../less/_variables.less';

	@bg-nagios: #DBDBDB;
	@all-up: #090;
	@some-up: #D60;
	@few-up: #D00;
	@normal: #090;
	@warning: #FC6;
	@critical: #D00;
	@unknown: #D60;

	.nagios-mini {
		width: 45%;
		height: 40%;
		padding: 2% 2% 1% 1%;

		.nagios-container {
			.padded-widget(@bg-nagios);
			font-family: @font-main;
			overflow: hidden;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
		}

		.nagios-summary {
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
			padding-bottom: 15px;

			&.problems {
				h2 {
					font-size: 1.6em;
				}

				.nagios-up-pct {
					font-size: 2.8em !important;
					line-height: 1em !important;
				}
			}

			h2 {
				font-size: 2.2em;
				width: 45%;
				font-weight: 700;
				margin: 0;
				transition: all 0.5s ease;
			}
		}

		.nagios-up-pct {
			display: inline-block;
			border-radius: 0.2em;
			font-size: 4em;
			line-height: 1em;
			font-weight: 700;
			padding: 0.2em;
			color: #fff;
			background-color: #666;
			transition: all 0.5s ease;

			&.all-up {
				background-color: @all-up;
			}

			&.some-up {
				background-color: @some-up;
			}

			&.few-up {
				background-color: @few-up;
			}
		}

		.nagios-all-up {

			h2 {
				font-size: 1.7em;
				margin: 0;
			}

			p {
				font-size: 1.7em;
				opacity: 0.6;
				margin: 0;
			}
		}

		.nagios-problems {
			display: flex;
			//flex-direction: column;
			justify-content: flex-start;
			align-items: flex-end;
			flex-wrap: wrap;
			margin-bottom: -15px;
			margin-right: -15px;
			overflow: hidden;
		}

		.nagios-host {
			margin: 0 10px 10px 0;

			h2 {
				font-size: 1em;
				line-height: 1em;
				margin: 0 0 0.3em;

				span.up {
					color: @normal;
				}

				span.down {
					color: @critical;
				//	animation: blink 1.5s infinite;
				}

				span.unreachable {
					color: @unknown;
				//	animation: blink 1.5s infinite;
				}
			}
			.services {
				display: flex;
				//flex-direction: column;
				justify-content: flex-start;
				align-items: flex-start;
				flex-wrap: wrap;
			}

			.service {
				font-size: 0.8em;
				line-height: 1em;
				max-width: 8em;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				padding: 0.2em 0.4em;
				border-radius: 0.3em;
				margin: 0 2px 2px 0;
				border: 1px solid #000;

				&.warning {
					background-color: @warning;
				//	animation: warning 1.5s infinite;
				}

				&.critical {
					color: #FFF;
					background-color: @critical;
				//	animation: critical 1.5s infinite;
				}

				&.unreachable,
				&.unknown {
					color: #FFF;
					background-color: @unknown;
				//	animation: unknown 1.5s infinite;
				}
			}
		}
	}
	/*
	@keyframes critical {
		0%   { background-color: @critical; }
		49% { background-color: @critical; }
		50% { background-color: #000; }
		99% { background-color: #000; }
		100% { background-color: @critical; }
	}

	@keyframes warning {
		0%   { background-color: @warning; color: #000; }
		49% { background-color: @warning; color: #000; }
		50% { background-color: #000; color: #FFF; }
		99% { background-color: #000; color: #FFF; }
		100% { background-color: @warning; color: #000; }
	}

	@keyframes unknown {
		0%   { background-color: @unknown; }
		49% { background-color: @unknown; }
		50% { background-color: #000; }
		99% { background-color: #000; }
		100% { background-color: @unknown; }
	}
	*/

</style>


// WEBPACK FOOTER //
// nagios-mini.vue?6d6ba90f